import { HomePageWidgetBlueprint } from '@backstage/plugin-home-react/alpha';
import Clocksvg from './clock-2.svg';
import { LinkButton, TrendLine } from '@backstage/core-components';
import { Box, Text, DialogTrigger, Popover, Button } from '@backstage/ui';

import useAsync from 'react-use/lib/useAsync';
import { Progress } from '@backstage/core-components';
import { MaxxInterimSummary, MaxxWeekSummary} from '../../api/MaxxApi';

import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { maxxApiRef, formatDate } from '../../api';

const MaxxInfoCardContent = ({ weekSummary, interimSummary }: { weekSummary?: MaxxWeekSummary, interimSummary?: MaxxInterimSummary }) => {
  const trendData = weekSummary?.bookings?.map(b => b.hoursPercentage) ?? [0, 0, 0, 0, 0, 0, 0];
  const today = weekSummary?.bookings?.[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];

  const anchor = new Date();
  const diffToMonday = anchor.getDay() === 0 ? -6 : 1 - anchor.getDay();
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() + diffToMonday);
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  const fmt = (d: Date) => `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
  const weekLabel = `${fmt(monday)} – ${fmt(friday)}`;

  return (
    <Box
      style={{
        height: '100%',
        backgroundColor: '#35abe2',
        borderRadius: '12px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {/* Header row */}
      <Box style={{ display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'center' }}>
        <img src={Clocksvg} alt="Clock Logo" style={{ width: '48px', height: '48px', flexShrink: 0 }} />
        <Box>
          <Text variant="title-medium" style={{ color: '#FFF', display: 'block' }}>MaXX</Text>
          <Text variant="body-small" style={{ color: 'rgba(255,255,255,0.8)', display: 'block' }}>{weekLabel}</Text>
        </Box>
      </Box>

      {/* Prominent TrendLine */}
      <Box style={{ width: '100%', flexGrow: 1 }}>
        <TrendLine color="purple" data={trendData} title="Hours this week (% of daily target)" />
      </Box>

      {/* Today summary + info popover */}
      {today && (
        <Box style={{ display: 'flex', flexDirection: 'row', gap: '16px', justifyContent: 'space-between', alignContent: 'space-between'  }}>
          <Text variant="body-small" style={{ color: '#FFF', display: 'block' }}>
            Today: <strong>{today.hoursBookedOnDay}h</strong> ({(today.hoursPercentage * 100).toFixed(0)}%)
          </Text>
          <Text variant="body-small" style={{ color: '#FFF', display: 'block' }}>
            FlexTime: <strong>{weekSummary?.accessFLEXTIME}h</strong>
          </Text>
          <DialogTrigger>
            <Button variant="tertiary" size="small" style={{ color: '#FFF',  height: '16px', minWidth: 0 }}>ℹ</Button>
            <Popover>
              <Box style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '200px' }}>
                <Text variant="title-x-small">MaXX Details</Text>
                {interimSummary ? (
                  <>
                    <Text variant="body-small">Annual leave: <strong>{interimSummary.annualLeave}d</strong></Text>
                    <Text variant="body-small">Target working days: <strong>{interimSummary.targetWorkingDaysInMonth}d</strong></Text>
                    <Text variant="body-small">Target hours: <strong>{interimSummary.targetWorkingHours}h</strong></Text>
                    <Text variant="body-small">FlexTime: <strong>{interimSummary.balances?.FLEXTIME}h</strong></Text>
                    <Text variant="body-small">Illness: <strong>{interimSummary.balances?.ILLNESS}d</strong></Text>
                    <Text variant="body-small">Leave: <strong>{interimSummary.balances?.LEAVE}d</strong></Text>
                    <Text variant="body-small">FlexTime limit: <strong>{interimSummary.flextimeLowerLimit}h / {interimSummary.flextimeUpperLimit}h</strong></Text>
                  </>
                ) : (
                  <Text variant="body-small">No details available</Text>
                )}
              </Box>
            </Popover>
          </DialogTrigger>
        </Box>
      )}
    </Box>
  );
};

export default function MaxxInfoCard() {
  const maxxApi = useApi(maxxApiRef);
  const currentDate = formatDate(new Date());
    
  const { value, loading, error } = useAsync(async (): Promise<{ week: MaxxWeekSummary, interim: MaxxInterimSummary } | undefined> => {
    const [week, interim] = await Promise.all([
      maxxApi.readWeekSummary(currentDate),
      maxxApi.readInterimSummary(),
    ]);
    return { week, interim };
  }, []);

  if (loading) {
    return <Progress />;
  } else if (error) {
    return <MaxxInfoCardContent />;
  }
  
  return (    
        <MaxxInfoCardContent
          weekSummary={value?.week}
          interimSummary={value?.interim}
           />    
  );
}

export const Actions = () => {
  const configApi = useApi(configApiRef);
  const baseUrl = configApi?.getString('home-module-cards.maxxBaseUrl');
  const currentDate = formatDate(new Date());

  return (
    <LinkButton
      to={`${baseUrl}/bookings/current/daily/${currentDate}`}
      target="_blank"
    >
      Open maXX
    </LinkButton>
  );
};

export const Content = MaxxInfoCard        

export const maxxWidget = HomePageWidgetBlueprint.make({
  name: 'maxx-widget',
  params: {
    name: 'MaxxWidget',        
    description: 'A Maxx widget for the homepage',
    components: () =>
      import('./MaxxInfoCard').then(m => ({
        Content: m.Content, 
        Actions: m.Actions 
      })),      
    layout: {
      height: { minRows: 5, defaultRows: 5 },
      width: { minColumns: 3, defaultColumns: 3 },
    },
  },
}) as any;

